import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { __ } from '@wordpress/i18n';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { generateFieldName, generateGroupId, getDefaultLabel, getDefaultPlaceholder, groupDefinitions } from '../utils/fieldTypes';

export const useDragAndDrop = ({ tabs, setTabs, showToast }) => {
  const [activeDragId, setActiveDragId] = useState(null);
  const [dragOverTabId, setDragOverTabId] = useState(null);
  const [dragDirection, setDragDirection] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isRequiredTab = (tab) => {
    return tab.required || tab.title === 'Basic Info';
  };

  const isFieldLocked = (tab, fieldId) => {
    const field = tab.fields.find(f => f.id === fieldId);
    return field?.locked === true;
  };

  const enforceRequiredTabPositions = (tabsArray) => {
    if (!Array.isArray(tabsArray)) return [];
    
    const basicInfoTab = tabsArray.find(tab => tab.title === 'Basic Info');
    const otherTabs = tabsArray.filter(tab => tab.title !== 'Basic Info');

    const orderedTabs = [];
    if (basicInfoTab) orderedTabs.push(basicInfoTab);
    orderedTabs.push(...otherTabs);

    return orderedTabs.map((tab, index) => ({
      ...tab,
      order: index
    }));
  };

  /**
   * Create group fields when dragging from component panel
   */
  const createGroupFields = (groupType, targetTab, existingFields) => {
    const groupDefinition = groupDefinitions[groupType];
    if (!groupDefinition) return [];
    
    const groupId = generateGroupId(groupType);
    const timestamp = Date.now();
    
    return groupDefinition.fields.map((fieldDef, index) => ({
      id: `${groupType}_${timestamp}_${fieldDef.id}_${uuidv4().substring(0, 8)}`,
      name: generateFieldName(fieldDef.prefix, fieldDef.id),
      type: fieldDef.type,
      label: fieldDef.label,
      placeholder: fieldDef.placeholder,
      required: fieldDef.required || false,
      adminOnly: false,
      enabled: true,
      locked: false,
      order: existingFields.length + index,
      group_id: groupId,
      group_type: groupType,
      group_name: groupDefinition.name,
      group_position: index,
      group_total: groupDefinition.fieldCount,
      description: fieldDef.description || ''
    }));
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveDragId(active.id);
    setDragDirection(null);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleDragMove = (event) => {
    const { delta } = event;
    if (delta) {
      setDragPosition({
        x: delta.x || 0,
        y: delta.y || 0
      });
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setDragOverTabId(null);
      return;
    }

    const overTab = tabs.find(tab => tab.id === over.id);
    if (overTab) {
      setDragOverTabId(overTab.id);
      return;
    }

    for (const tab of tabs) {
      if (tab.fields.some(field => field.id === over.id)) {
        setDragOverTabId(tab.id);
        
        const activeFieldIndex = tab.fields.findIndex(field => field.id === active.id);
        const overFieldIndex = tab.fields.findIndex(field => field.id === over.id);
        
        if (activeFieldIndex !== -1 && overFieldIndex !== -1) {
          setDragDirection(activeFieldIndex < overFieldIndex ? 'down' : 'up');
        }
        
        break;
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    setActiveDragId(null);
    setDragOverTabId(null);
    setDragDirection(null);
    setDragPosition({ x: 0, y: 0 });
    
    if (!over) return;

    // Handle adding new fields from component panel
    if (active.data.current?.source === 'component-panel') {
      const fieldType = active.data.current?.fieldType;
      let targetTabId = null;

      if (over.data.current?.type === 'tab') {
        targetTabId = over.id;
      } else {
        for (const tab of tabs) {
          if (tab.fields.some(field => field.id === over.id)) {
            targetTabId = tab.id;
            break;
          }
        }
      }

      if (!targetTabId && over.id.toString().includes('tab_content_')) {
        targetTabId = over.id.replace('tab_content_', '');
      }

      if (targetTabId && fieldType) {
        const targetTab = tabs.find(tab => tab.id === targetTabId);
        
        // Prevent adding fields to required tabs
        if (isRequiredTab(targetTab)) {
          showToast('error', __('Cannot Add Field', 'havenlytics'), __('Cannot add fields to required tabs', 'havenlytics'));
          return;
        }
        
        // Check if this is a group field type
        const fieldTypeDef = fieldTypes.find(ft => ft.value === fieldType);
        
        if (fieldTypeDef?.isGroup) {
          // Create all fields for the group
          const groupFields = createGroupFields(fieldTypeDef.groupType, targetTab, targetTab.fields);
          
          const newTabs = tabs.map(tab => 
            tab.id === targetTabId ? { 
              ...tab, 
              fields: [...tab.fields, ...groupFields] 
            } : tab
          );
          
          const enforcedTabs = enforceRequiredTabPositions(newTabs);
          setTabs(enforcedTabs);
          
          const groupName = groupDefinitions[fieldTypeDef.groupType]?.name || fieldTypeDef.groupType;
          showToast('success', __('Group Added', 'havenlytics'), `${groupName} ${__('group added to', 'havenlytics')} ${targetTab.title}`);
          return;
        }
        
        // Handle other field types normally
        const newField = {
          id: `field_${Date.now()}_${uuidv4().substring(0, 8)}`,
          type: fieldType,
          label: getDefaultLabel(fieldType),
          placeholder: getDefaultPlaceholder(fieldType),
          required: false,
          adminOnly: false,
          enabled: true,
          locked: false,
          order: targetTab.fields.length
        };
        
        const newTabs = tabs.map(tab => 
          tab.id === targetTabId ? { ...tab, fields: [...tab.fields, newField] } : tab
        );
        const enforcedTabs = enforceRequiredTabPositions(newTabs);
        setTabs(enforcedTabs);
        showToast('success', __('Field Added', 'havenlytics'), __('Field added to', 'havenlytics') + ` ${targetTab.title}`);
      }
      return;
    }
    
    // Handle tab sorting - PREVENT MOVING REQUIRED TABS
    if (active.id !== over.id) {
      const activeTabIndex = tabs.findIndex(tab => tab.id === active.id);
      const overTabIndex = tabs.findIndex(tab => tab.id === over.id);
              
      if (activeTabIndex !== -1 && overTabIndex !== -1) {
        const activeTab = tabs[activeTabIndex];
        const overTab = tabs[overTabIndex];
        
        // Prevent moving required tabs
        if (isRequiredTab(activeTab) || isRequiredTab(overTab)) {
          showToast('error', __('Cannot Move', 'havenlytics'), __('Required tabs (Basic Info) cannot be moved', 'havenlytics'));
          return;
        }
        
        // Allow moving non-required tabs
        const newTabs = arrayMove(tabs, activeTabIndex, overTabIndex);
        const enforcedTabs = enforceRequiredTabPositions(newTabs);
        setTabs(enforcedTabs);
        showToast('success', __('Tab Moved', 'havenlytics'), __('Tab position updated successfully', 'havenlytics'));
        return;
      }
      
      // Handle field sorting
      for (let tabIndex = 0; tabIndex < tabs.length; tabIndex++) {
        const tab = tabs[tabIndex];
        const activeFieldIndex = tab.fields.findIndex(field => field.id === active.id);
        
        if (activeFieldIndex !== -1) {
          // Prevent moving locked fields
          if (isFieldLocked(tab, active.id)) {
            showToast('error', __('Cannot Move', 'havenlytics'), __('Required fields cannot be moved', 'havenlytics'));
            return;
          }
          
          const overTabIndex = tabs.findIndex(t => t.fields.some(f => f.id === over.id));
          
          if (overTabIndex !== -1 && tabIndex === overTabIndex) {
            const overFieldIndex = tabs[overTabIndex].fields.findIndex(field => field.id === over.id);
            
            if (activeFieldIndex !== overFieldIndex && overFieldIndex !== -1) {
              const newTabs = JSON.parse(JSON.stringify(tabs));
              const movedFields = arrayMove(newTabs[tabIndex].fields, activeFieldIndex, overFieldIndex);
              
              newTabs[tabIndex].fields = movedFields.map((field, index) => ({
                ...field,
                order: index
              }));
              
              setTabs(newTabs);
              showToast('success', __('Field Moved', 'havenlytics'), __('Field position updated successfully', 'havenlytics'));
              return;
            }
          }
        }
      }
    }
  };

  return {
    sensors,
    activeDragId,
    dragOverTabId,
    dragDirection,
    dragPosition,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd
  };
};